"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Calendar, Clock, Users } from "lucide-react";
export default function CrewAndProductionScheduling() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  return <section ref={sectionRef} className="relative bg-light-background py-20 px-6 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        {}
        <div className={`text-center mb-12 transition-all duration-700 ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground-light mb-6">
            Crew and Production Scheduling
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Coordinate your entire production team with precision. Our
            intelligent scheduling system keeps everyone in sync, from crew
            members to equipment, ensuring seamless collaboration across all
            departments.
          </p>
        </div>

        {}
        <div className={`relative mb-12 transition-all duration-700 ease-[var(--ease-smooth)] delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
          <div className="bg-white rounded-lg shadow-soft p-8 border border-muted">
            <svg viewBox="0 0 800 300" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
              {}
              <line x1="50" y1="150" x2="750" y2="150" stroke="hsl(210, 78%, 50%)" strokeWidth="3" strokeLinecap="round" />

              {}
              <g>
                {}
                <circle cx="150" cy="150" r="20" fill="hsl(210, 78%, 50%)" className="animate-pulse" style={{
                animationDuration: "2s"
              }} />
                <text x="150" y="200" textAnchor="middle" className="text-sm font-semibold fill-foreground-light">
                  Pre-production
                </text>
                <rect x="135" y="135" width="30" height="30" fill="white" opacity="0.9" rx="4" />
                <path d="M145 145 L155 150 L145 155 Z M155 145 L155 155" stroke="hsl(210, 78%, 50%)" strokeWidth="2" fill="none" strokeLinecap="round" />

                {}
                <circle cx="350" cy="150" r="20" fill="hsl(210, 78%, 50%)" className="animate-pulse" style={{
                animationDuration: "2s",
                animationDelay: "0.3s"
              }} />
                <text x="350" y="200" textAnchor="middle" className="text-sm font-semibold fill-foreground-light">
                  Production
                </text>
                <rect x="335" y="135" width="30" height="30" fill="white" opacity="0.9" rx="4" />
                <circle cx="350" cy="145" r="4" fill="hsl(210, 78%, 50%)" />
                <circle cx="350" cy="155" r="4" fill="hsl(210, 78%, 50%)" />
                <circle cx="342" cy="150" r="4" fill="hsl(210, 78%, 50%)" />
                <circle cx="358" cy="150" r="4" fill="hsl(210, 78%, 50%)" />

                {}
                <circle cx="550" cy="150" r="20" fill="hsl(210, 78%, 50%)" className="animate-pulse" style={{
                animationDuration: "2s",
                animationDelay: "0.6s"
              }} />
                <text x="550" y="200" textAnchor="middle" className="text-sm font-semibold fill-foreground-light">
                  Post-production
                </text>
                <rect x="535" y="135" width="30" height="30" fill="white" opacity="0.9" rx="4" />
                <rect x="542" y="142" width="16" height="16" stroke="hsl(210, 78%, 50%)" strokeWidth="2" fill="none" rx="2" />
                <path d="M545 150 L548 153 L555 144" stroke="hsl(210, 78%, 50%)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />

                {}
                <circle cx="700" cy="150" r="20" fill="hsl(30, 90%, 55%)" className="animate-pulse" style={{
                animationDuration: "2s",
                animationDelay: "0.9s"
              }} />
                <text x="700" y="200" textAnchor="middle" className="text-sm font-semibold fill-foreground-light">
                  Delivery
                </text>
                <rect x="685" y="135" width="30" height="30" fill="white" opacity="0.9" rx="4" />
                <path d="M692 145 L708 145 L708 155 L692 155 Z M692 145 L700 140 L708 145" stroke="hsl(30, 90%, 55%)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </g>

              {}
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                  <polygon points="0 0, 10 3, 0 6" fill="hsl(210, 78%, 50%)" />
                </marker>
              </defs>
            </svg>

            {}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <div className="flex items-start gap-3 p-4 rounded-md bg-primary-light/50 border border-primary/20" data-index={0}>
                <SafeHydrate><Calendar className="size-5 text-primary shrink-0 mt-0.5" /></SafeHydrate>
                <div>
                  <h4 className="font-semibold text-sm text-foreground-light mb-1">
                    Smart Scheduling
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Automated conflict detection and resolution
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-md bg-primary-light/50 border border-primary/20" data-index={1}>
                <Clock className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm text-foreground-light mb-1">
                    Real-time Updates
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Instant notifications across all devices
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-md bg-primary-light/50 border border-primary/20" data-index={2}>
                <Users className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm text-foreground-light mb-1">
                    Team Coordination
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Centralized crew and resource management
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className={`text-center transition-all duration-500 ease-[var(--ease-spring)] delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" className="shadow-medium hover:shadow-strong hover:scale-102 transition-all duration-200">
            <Link to="#get-started" className="inline-flex items-center gap-2">
              Get Started with Scheduling
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}